import { useRuntimeConfig } from 'nuxt/app'
import { useAuthService } from '~/composables/useAuthService'

// Authenticated JSON fetch for the live-scoring protocol endpoints. These aren't standard
// CRUD (batched uploads, finalise, 409 conflict bodies), so useCrudApi doesn't fit.

export class ScoringApiError extends Error {
    status: number
    body: unknown

    constructor(status: number, body: unknown, message?: string) {
        super(message ?? `Request failed (${status})`)
        this.status = status
        this.body = body
    }
}

export const useScoringApi = () => {
    const config = useRuntimeConfig().public.grantThomasNuxt as { apiBaseUrl: string }
    const { getAccessToken } = useAuthService()

    const request = async <T>(method: string, path: string, body?: unknown): Promise<T> => {
        const token = getAccessToken()
        const response = await fetch(`${config.apiBaseUrl}${path}`, {
            method,
            headers: {
                'Content-Type': 'application/json',
                ...(token ? { Authorization: `Bearer ${token}` } : {}),
            },
            body: body === undefined ? undefined : JSON.stringify(body),
        })

        const parsed = await response.json().catch(() => null)
        if (!response.ok) throw new ScoringApiError(response.status, parsed)
        return parsed as T
    }

    return {
        startSession: (payload: Record<string, unknown>) =>
            request<Record<string, unknown>>('POST', '/api/v2/livescoringsessions', payload),
        getActiveSessionForFixture: async (fixtureId: string) => {
            try {
                return await request<Record<string, unknown>>('GET', `/api/v2/livescoringsessions/fixture/${fixtureId}`)
            } catch (e) {
                if (e instanceof ScoringApiError && e.status === 404) return null // no live session
                throw e
            }
        },
        getFinalSessionForFixture: async (fixtureId: string) => {
            try {
                return await request<Record<string, unknown>>('GET', `/api/v2/livescoringsessions/fixture/${fixtureId}/final`)
            } catch (e) {
                if (e instanceof ScoringApiError && e.status === 404) return null // never live-scored
                throw e
            }
        },
        getSession: (id: string, includeEvents = false) =>
            request<Record<string, unknown>>(
                'GET',
                `/api/v2/livescoringsessions/${id}${includeEvents ? '?includeEvents=true' : ''}`,
            ),
        getMySessions: (limit = 20) =>
            request<Record<string, unknown>[]>('GET', `/api/v2/livescoringsessions/mine?limit=${limit}`),
        appendEvents: (id: string, payload: Record<string, unknown>) =>
            request<{
                lastAppliedSequence: number
                missingSequences: number[]
                completed: boolean
                resolvedResultKey: string | null
            }>('POST', `/api/v2/livescoringsessions/${id}/events`, payload),
        finaliseSession: (id: string, payload: Record<string, unknown>) =>
            request<Record<string, unknown>>('POST', `/api/v2/livescoringsessions/${id}/finalise`, payload),
        abandonSession: (id: string, deviceId: string) =>
            request<Record<string, unknown>>('POST', `/api/v2/livescoringsessions/${id}/abandon`, {
                deviceId,
                events: [],
            }),
        getOutcomeOverrides: (
            scope: { leagueId?: string; ladderId?: string; tournamentId?: string; americanoId?: string },
            scoringSystemId?: string | null,
        ) => {
            const query = new URLSearchParams()
            if (scope.leagueId) query.set('leagueId', scope.leagueId)
            if (scope.ladderId) query.set('ladderId', scope.ladderId)
            if (scope.tournamentId) query.set('tournamentId', scope.tournamentId)
            if (scope.americanoId) query.set('americanoId', scope.americanoId)
            // stale overrides from a previously selected scoring system stay invisible
            if (scoringSystemId) query.set('scoringSystemId', scoringSystemId)
            return request<Record<string, unknown>[]>('GET', `/api/v2/scoringsystemoverrides?${query}`)
        },
        setOutcomeOverrides: (payload: Record<string, unknown>) =>
            request<Record<string, unknown>[]>('PUT', '/api/v2/scoringsystemoverrides', payload),
        getOverrideSources: (scoringSystemId: string, clubId: string) =>
            request<Record<string, unknown>[]>(
                'GET',
                `/api/v2/scoringsystemoverrides/sources?scoringSystemId=${scoringSystemId}&clubId=${clubId}`,
            ),
        getLeaderboard: (params: { eventKey: string; clubId?: string; sportId?: string; from?: string; to?: string; limit?: number }) => {
            const query = new URLSearchParams()
            query.set('eventKey', params.eventKey)
            if (params.clubId) query.set('clubId', params.clubId)
            if (params.sportId) query.set('sportId', params.sportId)
            if (params.from) query.set('from', params.from)
            if (params.to) query.set('to', params.to)
            if (params.limit) query.set('limit', String(params.limit))
            return request<Record<string, unknown>[]>('GET', `/api/v2/fixtureevents/leaderboard?${query}`)
        },
    }
}
